import {
  validateEmail,
  validateUserSignupInput,
  validateArtistFields,
  validateVenueFields,
  validatePromoterFields,
} from '../../validate'
import { UserSignupFormErrors } from './FanSignupForm/FanSignupForm'
import { ArtistSignupFormErrors } from './ArtistSignup/ArtistSignupFields'
import { VenueSignupFormErrors } from './VenueSignup/VenueSignupFields'
import currentUserQuery from '../../graphql/queries/user/currentUser.query'
import { useSearchParams } from '@remix-run/react'
import { PromoterSignupFormErrors } from './PromoterSignup/PromoterSignupFields'
import {
  useEmailExistsVerifiedLazyQuery,
  useSignUpMutation,
  useSignUpArtistMutation,
  useSignUpVenueMutation,
  useSignUpPromoterMutation,
  useCreateArtistMutation,
  useCreateVenueMutation,
} from '../../graphql/mutations/auth.mut.gen'
import {
  UserSignupInput,
  ArtistSignupInput,
  VenueSignupInput,
  PromoterSignupInput,
} from '../../graphql/types.gen'

export type ProfileInput =
  | UserSignupInput
  | ArtistSignupInput
  | VenueSignupInput

export const useOnEmailChange = (
  setEmail: (email: string) => void,
  errors: ArtistSignupFormErrors | VenueSignupFormErrors,
  setErrors: (errors: UserSignupFormErrors) => void,
) => {
  const [emailExistsVerified] = useEmailExistsVerifiedLazyQuery()

  return (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget
    if (e.currentTarget.name === 'email') {
      const { error, isValid } = validateEmail(value)

      if (isValid) {
        emailExistsVerified({ variables: { email: value } }).then((result) => {
          const exists = result.data?.emailExistsVerified
          if (exists) {
            setErrors({ email: 'This email is already taken' })
          }
        })
      } else {
        setErrors({ email: error })
      }

      if (isValid || value.length === 0) {
        setErrors({ ...errors, email: undefined })
      }
    }

    setEmail(value)
  }
}

export const useOnUserSubmit = (
  inputs: { userInput: UserSignupInput },
  setUserErrors: (errors: UserSignupFormErrors) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const [search] = useSearchParams()
  const [signUpUser, signupResult] = useSignUpMutation()
  const { userInput } = inputs

  const permissionInvitationToken = search.get('permissionInvitationToken')
  if (permissionInvitationToken) {
    userInput['permissionInvitationToken'] = permissionInvitationToken
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { errors: userErrors, isValid: isUserValid } =
      validateUserSignupInput(userInput)

    if (isUserValid) {
      setUserErrors({})
      const signUpPromise = signUpUser({ variables: { input: userInput } })
      signUpPromise
        .then(() => {
          setSubmitted(true)
        })
        .catch((err) => console.error(err))
    } else {
      setUserErrors(userErrors)
    }
  }

  return {
    onSubmit,
    signupResult,
  }
}

export const useOnArtistSubmit = (
  inputs: {
    artistInput: ArtistSignupInput
    userInput: UserSignupInput
  },
  setUserErrors: (errors: UserSignupFormErrors) => void,
  setArtistErrors: (errors: ArtistSignupFormErrors) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const eventInvitationToken = useEventInvitationToken()
  const [signUpArtist, signupResult] = useSignUpArtistMutation()
  const { artistInput, userInput } = inputs

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { errors: userErrors, isValid: isUserValid } =
      validateUserSignupInput(userInput)
    const { errors: artistErrors, isValid: isArtistValid } =
      validateArtistFields(artistInput)

    if (isUserValid && isArtistValid) {
      setUserErrors({})
      setArtistErrors({})
      const signUpPromise = signUpArtist({
        variables: {
          artist: {
            ...artistInput,
            eventInvitationToken,
          },
          user: userInput,
        },
      })
      signUpPromise
        .then(() => {
          setSubmitted(true)
        })
        .catch((err) => console.error(err))
    } else {
      setUserErrors(userErrors)
      setArtistErrors(artistErrors)
    }
  }

  return { onSubmit, signupResult }
}

export const useOnVenueSubmit = (
  inputs: {
    venueInput: VenueSignupInput
    userInput: UserSignupInput
  },
  setUserErrors: (errors: UserSignupFormErrors) => void,
  setVenueErrors: (errors: VenueSignupFormErrors) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const eventInvitationToken = useEventInvitationToken()

  const [signUpVenue, signupResult] = useSignUpVenueMutation()
  const { venueInput, userInput } = inputs

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { errors: userErrors, isValid: isUserValid } =
      validateUserSignupInput(userInput)
    const { errors: venueErrors, isValid: isVenueValid } =
      validateVenueFields(venueInput)

    if (isUserValid && isVenueValid) {
      setUserErrors({})
      setVenueErrors({})
      const signUpPromise = signUpVenue({
        variables: {
          venue: {
            ...venueInput,
            eventInvitationToken,
          },
          user: userInput,
        },
      })
      signUpPromise
        .then(() => {
          setSubmitted(true)
        })
        .catch((err) => console.error(err))
    } else {
      setUserErrors(userErrors)
      setVenueErrors(venueErrors)
    }
  }

  return { onSubmit, signupResult }
}

export const useOnPromoterSubmit = (
  inputs: {
    promoter: PromoterSignupInput
    user: UserSignupInput
  },
  setUserErrors: (errors: UserSignupFormErrors) => void,
  setPromoterErrors: (errors: PromoterSignupFormErrors) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const eventInvitationToken = useEventInvitationToken()
  const [signUpPromoter, signupResult] = useSignUpPromoterMutation()
  const { promoter, user } = inputs

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { errors: userErrors, isValid: isUserValid } =
      validateUserSignupInput(user)
    const { errors: promoterErrors, isValid: isPromoterValid } =
      validatePromoterFields(promoter)

    if (isUserValid && isPromoterValid) {
      setUserErrors({})
      setPromoterErrors({})
      const signUpPromise = signUpPromoter({
        variables: {
          promoter: {
            ...promoter,
            eventInvitationToken,
          },
          user,
        },
      })
      signUpPromise
        .then(() => {
          setSubmitted(true)
        })
        .catch((err) => console.error(err))
    } else {
      setUserErrors(userErrors)
      setPromoterErrors(promoterErrors)
    }
  }

  return { onSubmit, signupResult }
}

export const useOnArtistCreation = (
  inputs: { artistInput: ArtistSignupInput },
  setArtistErrors: (errors: ArtistSignupFormErrors) => void,
  setIsLoading: (isLoading: boolean) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const { artistInput } = inputs

  const [createArtist] = useCreateArtistMutation()

  return (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    const { errors: artistErrors, isValid: isArtistValid } =
      validateArtistFields(artistInput)

    if (isArtistValid) {
      setArtistErrors({})
      const signUpPromise = createArtist({
        variables: { input: artistInput },
        refetchQueries: [{ query: currentUserQuery }],
      })
      signUpPromise
        .then(() => {
          setSubmitted(true)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          setIsLoading(false)
        })
    } else {
      setArtistErrors(artistErrors)
      setIsLoading(false)
    }
  }
}

export const useOnVenueCreation = (
  inputs: { venueInput: VenueSignupInput },
  setVenueErrors: (errors: VenueSignupFormErrors) => void,
  setIsLoading: (isLoading: boolean) => void,
  setSubmitted: (isSubmitted: boolean) => void,
) => {
  const { venueInput } = inputs
  const [createVenue] = useCreateVenueMutation()

  return (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    const { errors: venueErrors, isValid: isVenueValid } =
      validateVenueFields(venueInput)

    if (isVenueValid) {
      setVenueErrors({})
      const signUpPromise = createVenue({
        variables: { input: venueInput },
        refetchQueries: [{ query: currentUserQuery }],
      })
      signUpPromise
        .then(() => {
          setSubmitted(true)
          setIsLoading(false)
        })
        .catch((err) => console.error(err))
    } else {
      setVenueErrors(venueErrors)
      setIsLoading(false)
    }
  }
}

function useEventInvitationToken() {
  const [searchParams] = useSearchParams()
  return searchParams.get('eventInvitationToken')
}
